import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatStamp } from "@/lib/format";
import { History, Loader2, CheckCircle2 } from "lucide-react";

/** Calculates the earlier months in the background so the trend has real history. */
export default function BackfillMonths({ months = 6, onDone }) {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/billing-run/backfill/status");
      setJob(data.status === "idle" ? null : data);
      return data.status;
    } catch { return "idle"; }
  }, []);

  useEffect(() => {
    let stop = false;
    const tick = async () => {
      const status = await poll();
      if (!stop && status === "running") setTimeout(tick, 5000);
      if (!stop && status === "done") onDone?.();
    };
    tick();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poll]);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post(`/admin/billing-run/backfill?months=${months}`);
      toast.success(data.total ? `Calculating ${data.total} client-months in the background` : "Every month is already calculated");
      const tick = async () => {
        const s = await poll();
        if (s === "running") setTimeout(tick, 5000);
        else onDone?.();
      };
      tick();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setStarting(false); }
  };

  const running = job?.status === "running";
  const pct = running && job.total ? Math.round((job.done / job.total) * 100) : 0;

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3" data-testid="backfill-months">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-[240px]">
          <p className="text-sm font-semibold text-slate-800 inline-flex items-center gap-2">
            <History className="w-4 h-4 text-slate-500" /> Fill in the history
          </p>
          <p className="text-xs text-slate-500 mt-0.5" data-testid="backfill-status">
            {running
              ? <>Calculating {job.current || "…"} · <span data-testid="backfill-count">{job.done} of {job.total}</span></>
              : job?.status === "interrupted"
                ? "Last backfill stopped early — press the button to finish it."
                : job?.finished_at
                  ? job.calculated
                    ? <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> {job.calculated} months calculated as of {formatStamp(job.finished_at)}</span>
                    : <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Every month is already calculated</span>
                  : `Calculates every client's last ${months} months. Runs in the background — one client at a time to respect ShipStation's rate limit.`}
          </p>
        </div>
        <button onClick={start} disabled={running || starting} data-testid="backfill-btn"
          className="inline-flex items-center gap-2 border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-60 text-slate-800 font-semibold px-3.5 py-2 rounded-md text-sm">
          {running || starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <History className="w-4 h-4" />}
          Backfill last {months} months
        </button>
      </div>
      {running && (
        <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden" data-testid="backfill-progress">
          <div className="h-full bg-blue-600 transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      )}
      {(job?.errors || []).length > 0 && (
        <ul className="mt-2 text-xs text-red-700 space-y-0.5" data-testid="backfill-errors">
          {job.errors.slice(0, 5).map((e) => <li key={e.company_name || e.account_id}>{e.company_name}: {e.error}</li>)}
        </ul>
      )}
    </div>
  );
}
